import { useGetMe } from 'api-hooks/auth/query';
import { DocumentTypeEnum } from 'api-hooks/order/travel-document';
import { TravelDocumentSalesModel } from 'api-hooks/sale/travel-document';
import { VisaDocumentModel } from 'api-hooks/visa-document';
import { generateId } from 'common/utils/string';
import {
  guestNameTransformer,
  preferencesTransformer,
} from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import React from 'react';

import {
  DocumentSaleAddOnsFormType,
  DocumentSaleGuestDetailFormType,
  DocumentSaleGuestTypeFormType,
  DocumentSalesFormType,
  DocumentSaleFormType,
} from '../components/form-type';

interface Props {
  travelDocument?: TravelDocumentSalesModel;
  defaultValues?: DocumentSaleFormType;
}

export function addNewAddOns(): DocumentSaleAddOnsFormType {
  return {
    customId: generateId(),
    description: '',
    guestId: '',
    guestName: '',
    id: undefined,
    price: undefined,
  } as never as DocumentSaleAddOnsFormType;
}

export function addNewGuestDetail(
  type = DocumentTypeEnum.visa,
): DocumentSaleGuestDetailFormType {
  const defaultValues = {
    customId: generateId(),
    id: undefined,
    description: '',
    price: undefined,
    label: '',
    appointmentAt: new Date(),
    biometric: false,
    type: '',
    startAt: new Date(),
    endAt: new Date(),
    days: 1,
    package: '',
    company: '',
    coverArea: '',
    visaDocumentId: '',
  } as never as DocumentSaleGuestDetailFormType;

  return defaultValues;
}

export function addNewGuest(
  type = DocumentTypeEnum.visa,
): DocumentSaleGuestTypeFormType {
  return {
    customId: generateId(),
    id: undefined,
    guestId: '',
    guestName: '',
    preferences: [],
    details: [addNewGuestDetail(type)],
  };
}

export function addNewDocumentSale(
  type = DocumentTypeEnum.visa,
): DocumentSalesFormType {
  return {
    customId: generateId(),
    id: undefined,
    documentType: type,
    documentSaleAddOns: [],
    reminder: false,
    reminderDate: null,
    reminderDescription: '',
    documentReceiveDate: null,
    visaDocuments: [],
    documentSaleGuests: [addNewGuest(type)],
  };
}

export function visaDocumentToDocumentSales(
  visaDocument: VisaDocumentModel,
): DocumentSaleFormType {
  const documentSales: DocumentSalesFormType[] = [
    {
      ...addNewDocumentSale(),
      visaDocuments: [
        {
          id: visaDocument.id,
          label: visaDocument.name,
          extra: visaDocument,
        } as any,
      ],
      documentSaleGuests: [
        {
          ...addNewGuest(),
          details: [
            {
              ...addNewGuestDetail(),
              visaDocumentId: visaDocument.id,
              label: visaDocument.name,
              price: visaDocument.price,
            },
          ],
        },
      ],
    },
  ];

  const defaultValues: DocumentSaleFormType = {
    address: '',
    adminId: '',
    code: '',
    customerBillingId: '',
    customerBillingOptions: [],
    customerId: '',
    customerStatus: '',
    description: '',
    documentAdminId: '',
    discount: 0,
    documentSales,
    hasDueReceivable: false,
    remainingTransactionQuota: null,
    isExceedingQuota: false,
    needApproval: false,
    number: '',
    operatorId: '',
    otherCost: 0,
    phone: '',
    refId: undefined,
    parentRefId: '',
    salesmanId: '',
    tempTransactionAt: new Date(),
    total: 0,
    transactionAt: new Date(),
    travelDocument: undefined,
  };
  return defaultValues;
}

export default function useGetDefaultValues(props: Props) {
  const { travelDocument } = props;
  const { data: employeeData } = useGetMe();
  const employee = employeeData?.data;

  const defaultValues = React.useMemo<DocumentSaleFormType>(() => {
    const transactionAt = travelDocument?.transactionAt || new Date();
    if (props.defaultValues) {
      return {
        ...props.defaultValues,
        transactionAt,
        tempTransactionAt: transactionAt,
        documentSales: props.defaultValues.documentSales.map((ds) => {
          return {
            ...ds,
            id: undefined,
            documentSaleAddOns: ds.documentSaleAddOns.map((dsao) => {
              return {
                ...dsao,
                id: undefined,
              };
            }),
            documentSaleGuests: ds.documentSaleGuests.map((dsg) => {
              return {
                ...dsg,
                id: undefined,
              };
            }),
          };
        }),
        refId: undefined,
        travelDocument: undefined,
      };
    }

    const customer = travelDocument?.customer;

    // const visaDocumentOptions: VisaDocumentOptionType[] =
    //   travelDocument?.documentSales
    //     ?.map((ds) => {
    //       return (ds.visaDocuments || [])
    //         .map((vd) => {
    //           return {
    //             id: vd.id,
    //             label: vd.name,
    //             visaDocumentId: vd.id,
    //             price: vd.price || 0,
    //             preferenceId: vd.preference.id,
    //           };
    //           // MULTIPRICE
    //           // return (vd.visaDocumentItems || []).map((vdi) => {
    //           //   const option: VisaDocumentOptionType = {
    //           //     id: vdi.id,
    //           //     label: [vd.code, vd.name, vdi.preference.name].join(' - '),
    //           //     preferenceId: vdi.preference.id,
    //           //     price: vdi.price,
    //           //     visaDocumentItemId: vdi.id,
    //           //   };
    //           //   return option;
    //           // });
    //         })
    //         .flat();
    //     })
    //     .flat() ?? [];

    const documentSales: DocumentSalesFormType[] = (() => {
      return (
        travelDocument?.documentSales?.map((ds) => {
          const documentSaleAddOns: DocumentSaleAddOnsFormType[] =
            ds.documentSaleAddOns.map((dsao) => {
              const guest = dsao.guest;
              return {
                customId: generateId(),
                id: dsao.id,
                description: dsao.description,
                guestId: guest.id,
                guestName: guestNameTransformer(guest),
                price: dsao.price,
              };
            });

          const documentSaleGuests: DocumentSaleGuestTypeFormType[] =
            ds.documentSaleGuests.map((dsg) => {
              const guest = dsg.guest;
              const documentSaleGuest = {
                customId: generateId(),
                id: dsg.id,
                guestId: guest.id,
                guestName: guestNameTransformer(guest),
                preferences: preferencesTransformer(dsg.preferences || []),
              };
              if (Array.isArray(dsg.details)) {
                const details: DocumentSaleGuestDetailFormType[] =
                  dsg.details.map((detail) => {
                    const visaDocument = detail?.visaDocument;
                    return {
                      id: detail.id,
                      appointmentAt: detail.appointmentAt,
                      biometric: detail.biometric || false,
                      company: detail.company,
                      coverArea: detail.coverArea,
                      customId: generateId(),
                      days: detail.days,
                      description: detail.description || '',
                      endAt: detail.endAt,
                      package: detail.package,
                      visaDocumentId: visaDocument?.id,
                      label: visaDocument?.name || '',
                      price: detail.price || visaDocument?.price || 0,
                      startAt: detail.startAt,
                      type: detail.type,
                    } as never as DocumentSaleGuestDetailFormType;
                  });
                return {
                  ...documentSaleGuest,
                  details,
                };
              } else {
                const visaDocument = dsg.details.visaDocument;
                const details: DocumentSaleGuestDetailFormType[] = [
                  {
                    id: dsg.details.id,
                    appointmentAt: dsg.details.appointmentAt,
                    biometric: dsg.details.biometric || false,
                    company: dsg.details.company,
                    coverArea: dsg.details.coverArea,
                    customId: generateId(),
                    days: dsg.details.days,
                    description: dsg.details.description || '',
                    endAt: dsg.details.endAt,
                    package: dsg.details.package,
                    visaDocumentId: visaDocument?.id,
                    label: visaDocument?.name || '',
                    price: dsg.details.price || visaDocument?.price || 0,
                    startAt: dsg.details.startAt,
                    type: dsg.details.type,
                  } as never as DocumentSaleGuestDetailFormType,
                ];
                return {
                  ...documentSaleGuest,
                  details,
                };
              }
            });

          return {
            customId: generateId(),
            documentType: ds.documentType || '',
            id: ds.id,
            reminder: ds.reminder || false,
            reminderDate: ds.reminderDate || null,
            reminderDescription: ds.reminderDescription || '',
            documentReceiveDate: ds.documentReceiveDate,
            documentSaleAddOns,
            documentSaleGuests,
            visaDocuments: (ds.visaDocuments || []).map((vd) => {
              return {
                id: vd.id,
                label: vd.name,
                extra: vd as any,
              };
            }),
          };
        }) ?? [addNewDocumentSale()]
      );
    })();

    return {
      address: customer?.address || '',
      adminId: travelDocument?.admin?.id || employee?.id || '',
      code: travelDocument?.code || '',
      customerBillingId: travelDocument?.customerBillingAddress?.id || '',
      customerBillingOptions: [],
      customerId: customer?.id || '',
      customerStatus: customer?.status || '',
      description: travelDocument?.description || '',
      discount: travelDocument?.discount || 0,
      hasDueReceivable: customer?.hasDueReceivables || false,
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      needApproval: false,
      tempTransactionAt: transactionAt,
      number: travelDocument?.invoiceNumber ?? '',
      operatorId: travelDocument?.operator?.id || '',
      otherCost: travelDocument?.otherCost || 0,
      documentAdminId: travelDocument?.documentAdmin?.id || '',
      phone: customer?.phone || '',
      refId: travelDocument?.refId,
      parentRefId: travelDocument?.parentSale?.refId,
      salesmanId: travelDocument?.salesman?.id || '',
      total: travelDocument?.total || 0,
      transactionAt,
      documentSales,
      travelDocument,
    };
  }, [employee?.id, props.defaultValues, travelDocument]);

  return defaultValues;
}
